import { createContext, type FC, useMemo, useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import SavedConfigurationsTable from "~/features/savedConfigurations/components/SavedConfigurationsTable";
import { type SavedConfiguration } from "~/features/configurator/Linear/models";
import { apiDeleteSavedConfig } from "~/features/configurator/Linear/api/savedConfigDelete";
import { DefaultConfirmationContext } from "~/features/configurator/Linear/components/ConfirmationContext";

import { apiUpdateSavedConfigName } from "~/features/configurator/Linear/api/savedConfigUpdateName";

import { apiShareConfigurationViaSalesPartnerFinder } from "~/features/configurator/Linear/api/savedConfigShare";
import { userIsCustomer, userIsRegistered } from "~/domains/user/utils/roles";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { ArrowLink } from "~/common/components/Link";
import ShareConfigurationsViaEmailPopup from "~/features/savedConfigurations/components/ShareConfigurationsViaEmailPopup";
import { GET_ORDER_DESIGNTION_TEXT } from "~/features/configurator/Linear/constants";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { Button, Stack } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";

export interface SavedConfigurationItem extends SavedConfiguration {
  isBeingDeleted?: boolean;
  isBeingUpdated?: boolean;
}

interface SavedConfigContextProps {
  updateItem: (code: string, newName: string, newComment: string) => void;
  deleteItem: (code: string) => void;
  selectItem: (code: string, newValue: boolean) => void;
  toggleComment: (code: string, newValue: boolean) => void;
  shareItemsViaSalesPartnerFinder: (item: SavedConfiguration[]) => void;
}

export const SavedConfigContext = createContext<SavedConfigContextProps>({
  updateItem: () => {},
  deleteItem: () => {},
  selectItem: () => {},
  toggleComment: () => {},
  shareItemsViaSalesPartnerFinder: () => {},
});

interface Props {
  initialItems: SavedConfiguration[];
}

const DISCLAIMER_IDS = ["34234361739", "34234363915"];

export const SavedConfigurationsView: FC<Props> = ({ initialItems }) => {
  const { t } = useTranslation();

  const [items, setItems] = useState<SavedConfigurationItem[]>(initialItems);

  const isCustomer = userIsCustomer();

  const onSelectAll = () => {
    setItems((items) => {
      const isAllSelected = items.every((item) => item.isSelected);
      return items.map((item) => ({ ...item, isSelected: !isAllSelected }));
    });
  };

  const contextData: SavedConfigContextProps = useMemo(
    () => ({
      deleteItem: (code: string) => {
        setItems((items) => items.map((item) => (item.code === code ? { ...item, isBeingDeleted: true } : item)));

        apiDeleteSavedConfig(code).then((success) => {
          if (success) {
            setItems((items) => items.filter((item) => item.code !== code));
          } else {
            setItems((items) => items.map((item) => (item.code === code ? { ...item, isBeingDeleted: false } : item)));
          }
        });
      },
      selectItem: (code: string, newVal: boolean) => {
        setItems((items) => {
          return items.map((item) => (item.code === code ? { ...item, isSelected: newVal } : item));
        });
      },
      toggleComment: (code: string, newVal: boolean) => {
        setItems((items) => {
          return items.map((item) => (item.code === code ? { ...item, showComment: newVal } : item));
        });
      },
      updateItem: (code: string, newName: string, newComment: string) => {
        setItems((items) => items.map((item) => (item.code === code ? { ...item, isBeingUpdated: true } : item)));
        apiUpdateSavedConfigName(code, newName, newComment).then((success) => {
          setItems((items) =>
            items.map((item) =>
              item.code === code
                ? {
                    ...item,
                    name: success ? newName : item.name,
                    comment: success ? newComment : item.comment,
                    isBeingUpdated: false,
                  }
                : item,
            ),
          );
        });
      },
      shareItemsViaSalesPartnerFinder: (sharedItems: SavedConfiguration[]) => {
        apiShareConfigurationViaSalesPartnerFinder(sharedItems);
      },
    }),
    [],
  );

  const deleteSelected = () => {
    items.forEach((item) => {
      if (item.isSelected && !item.isBeingDeleted) {
        contextData.deleteItem(item.code);
      }
    });
  };

  const itemsToBeShared: SavedConfiguration[] = items.filter((item) => item.isSelected && !item.isBeingDeleted);

  const shareSelectedViaSalesFinder = () => {
    if (itemsToBeShared.length > 0) {
      contextData.shareItemsViaSalesPartnerFinder(itemsToBeShared);
    }
  };

  const disableDeleteSelected =
    items.length === 0 ||
    items.some((item) => item.isSelected && item.isBeingDeleted) ||
    !items.some((item) => item.isSelected);

  const [isShareConfigurationsViaEmailPopupOpen, setIsShareConfigurationsViaEmailPopupOpen] = useState(false);

  const disclaimerId = GET_ORDER_DESIGNTION_TEXT(DISCLAIMER_IDS[userIsRegistered() ? 1 : 0]);

  return (
    <>
      <PageTitle title={t("page.savedLinearConfigurations.title")} />

      {items.length > 0 && (
        <div className={"mb-30"}>
          <Alert severity={"info"} children={<LanguageAwareExternalContent content={disclaimerId} removePtags />} />
        </div>
      )}

      <div className="d-flex flex-justify-end my-30">
        <Stack direction={"row"} gap={2}>
          <ArrowLink
            onClick={() => {
              setIsShareConfigurationsViaEmailPopupOpen(true);
            }}
            disabled={disableDeleteSelected}
          >
            {t("page.savedConfigurations.link.shareSelected")}
          </ArrowLink>
          {isShareConfigurationsViaEmailPopupOpen && (
            <ShareConfigurationsViaEmailPopup
              onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
              items={itemsToBeShared}
              onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
            />
          )}
          {canSeeSalesFinder() && !isCustomer && (
            <>
              {isShareConfigurationsViaEmailPopupOpen && (
                <ShareConfigurationsViaEmailPopup
                  onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                  items={itemsToBeShared}
                  onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                />
              )}
            </>
          )}
          {canSeeSalesFinder() && !isCustomer && (
            <Button disabled={disableDeleteSelected} onClick={shareSelectedViaSalesFinder}>
              {t("page.savedLinearConfigurations.table.button.share.name")}
            </Button>
          )}
          <DefaultConfirmationContext onConfirm={deleteSelected}>
            {(openConfirmation) => (
              <Button onClick={openConfirmation} disabled={disableDeleteSelected}>
                {t("page.savedLinearConfigurations.table.button.name")}
              </Button>
            )}
          </DefaultConfirmationContext>
        </Stack>
      </div>
      <SavedConfigContext.Provider value={contextData}>
        <SavedConfigurationsTable items={items} onSelectAll={onSelectAll} />
      </SavedConfigContext.Provider>
    </>
  );
};
